"use client";

import React, { createContext, useContext, useCallback, useMemo } from "react";
import {
  SimpleColumn,
  MobileLayoutPreset,
  MobileLayoutConfig,
  ColumnManagerContextType,
} from "./types";
import { useColumnStorage } from "./useColumnStorage";

export const ColumnManagerContext = createContext<ColumnManagerContextType | undefined>(undefined);

interface ColumnManagerProviderProps {
  children: React.ReactNode;
  storageKey: string;
  defaultColumns: SimpleColumn[];
}

export function ColumnManagerProvider({
  children,
  storageKey,
  defaultColumns,
}: ColumnManagerProviderProps) {
  const { columns, columnOrder, isLoading, setColumns, setColumnOrder } = useColumnStorage(
    storageKey,
    defaultColumns
  );

  const [mobileLayoutPreset, setMobileLayoutPreset] =
    React.useState<MobileLayoutPreset>("detailed");
  const [mobileLayoutConfig, setMobileLayoutConfig] = React.useState<MobileLayoutConfig>({
    cardLayout: {
      primaryFields: 2,
      secondaryFields: 3,
      badgeFields: 2,
      showActions: true,
    },
    drawerLayout: {
      groupBy: "category",
      showActions: true,
      actionLayout: "buttons",
    },
  });

  // Computed values
  const visibleColumns = useMemo(() => columns.filter((col) => col.visible), [columns]);

  const hiddenColumns = useMemo(() => columns.filter((col) => !col.visible), [columns]);

  // Actions
  const toggleColumn = useCallback(
    (columnId: string) => {
      setColumns((prev) =>
        prev.map((col) => (col.id === columnId ? { ...col, visible: !col.visible } : col))
      );
    },
    [setColumns]
  );

  const showAllColumns = useCallback(() => {
    setColumns((prev) => prev.map((col) => ({ ...col, visible: true })));
  }, [setColumns]);

  const hideAllColumns = useCallback(() => {
    setColumns((prev) => prev.map((col) => ({ ...col, visible: false })));
  }, [setColumns]);

  const resetToDefaults = useCallback(() => {
    setColumns(defaultColumns);
  }, [defaultColumns, setColumns]);

  const toggleCategory = useCallback(
    (category: string) => {
      const categoryColumns = columns.filter((col) => col.category === category);
      const visibleCount = categoryColumns.filter((col) => col.visible).length;
      const shouldShow = visibleCount < categoryColumns.length;

      setColumns((prev) =>
        prev.map((col) => (col.category === category ? { ...col, visible: shouldShow } : col))
      );
    },
    [columns, setColumns]
  );

  // Utilities
  const getColumn = useCallback((id: string) => columns.find((col) => col.id === id), [columns]);

  const getCategoryColumns = useCallback(
    (category: string) => columns.filter((col) => col.category === category),
    [columns]
  );

  const getCategoryStats = useCallback(
    (category: string) => {
      const categoryColumns = columns.filter((col) => col.category === category);
      const visibleCount = categoryColumns.filter((col) => col.visible).length;

      return {
        total: categoryColumns.length,
        visible: visibleCount,
        hidden: categoryColumns.length - visibleCount,
      };
    },
    [columns]
  );

  // Mobile layout methods
  const getMobileCardLayout = useCallback(
    (layoutType: "compact" | "detailed" = "detailed") => {
      const visibleCols = visibleColumns;

      // Sort by mobile priority or regular priority (create a copy to avoid mutation)
      const sortedCols = [...visibleCols].sort(
        (a, b) => (a.mobilePriority || a.priority) - (b.mobilePriority || b.priority)
      );

      const config = mobileLayoutConfig.cardLayout;

      if (layoutType === "compact") {
        return {
          primaryFields: sortedCols
            .filter((col) => col.mobileType === "primary" || !col.mobileType)
            .slice(0, 1),
          secondaryFields: sortedCols.filter((col) => col.mobileType === "secondary").slice(0, 1),
          badgeFields: sortedCols.filter((col) => col.mobileType === "badge").slice(0, 1),
        };
      }

      return {
        primaryFields: sortedCols
          .filter((col) => col.mobileType === "primary" || !col.mobileType)
          .slice(0, config.primaryFields),
        secondaryFields: sortedCols
          .filter((col) => col.mobileType === "secondary")
          .slice(0, config.secondaryFields),
        badgeFields: sortedCols
          .filter((col) => col.mobileType === "badge")
          .slice(0, config.badgeFields),
      };
    },
    [visibleColumns, mobileLayoutConfig]
  );

  const getMobileDrawerSections = useCallback(
    (groupBy: "category" | "type" | "none" = "category") => {
      const visibleCols = visibleColumns.filter((col) => col.mobileType !== "hidden");

      if (groupBy === "none") {
        return { "All Fields": visibleCols };
      }

      if (groupBy === "category") {
        const sections: { [key: string]: SimpleColumn[] } = {};
        visibleCols.forEach((col) => {
          if (!sections[col.category]) {
            sections[col.category] = [];
          }
          sections[col.category].push(col);
        });
        return sections;
      }

      // Group by type
      const sections: { [key: string]: SimpleColumn[] } = {
        primary: visibleCols.filter((col) => col.mobileType === "primary" || !col.mobileType),
        secondary: visibleCols.filter((col) => col.mobileType === "secondary"),
        badges: visibleCols.filter((col) => col.mobileType === "badge"),
      };

      return sections;
    },
    [visibleColumns]
  );

  const setMobileLayoutPresetCallback = useCallback(
    (preset: MobileLayoutPreset) => {
      setMobileLayoutPreset(preset);

      // Apply preset configurations
      const presets: Record<MobileLayoutPreset, MobileLayoutConfig> = {
        compact: {
          cardLayout: { primaryFields: 1, secondaryFields: 1, badgeFields: 1, showActions: true },
          drawerLayout: { groupBy: "type", showActions: true, actionLayout: "list" },
        },
        detailed: {
          cardLayout: { primaryFields: 2, secondaryFields: 3, badgeFields: 2, showActions: true },
          drawerLayout: { groupBy: "category", showActions: true, actionLayout: "buttons" },
        },
        minimal: {
          cardLayout: { primaryFields: 1, secondaryFields: 0, badgeFields: 1, showActions: false },
          drawerLayout: { groupBy: "none", showActions: false, actionLayout: "list" },
        },
        custom: mobileLayoutConfig,
      };

      setMobileLayoutConfig(presets[preset]);
    },
    [mobileLayoutConfig]
  );

  const getMobileLayoutPresetCallback = useCallback(() => mobileLayoutPreset, [mobileLayoutPreset]);

  const value: ColumnManagerContextType = useMemo(
    () => ({
      // State
      columns,
      visibleColumns,
      hiddenColumns,
      columnOrder,
      isLoading,
      storageKey,
      mobileLayoutPreset,
      mobileLayoutConfig,

      // Actions
      toggleColumn,
      showAllColumns,
      hideAllColumns,
      resetToDefaults,
      toggleCategory,
      setColumns,
      setColumnOrder,

      // Mobile layout methods
      getMobileCardLayout,
      getMobileDrawerSections,
      setMobileLayoutPreset: setMobileLayoutPresetCallback,
      getMobileLayoutPreset: getMobileLayoutPresetCallback,

      // Utilities
      getColumn,
      getCategoryColumns,
      getCategoryStats,
    }),
    [
      columns,
      visibleColumns,
      hiddenColumns,
      columnOrder,
      isLoading,
      storageKey,
      mobileLayoutPreset,
      mobileLayoutConfig,
      toggleColumn,
      showAllColumns,
      hideAllColumns,
      resetToDefaults,
      toggleCategory,
      setColumns,
      setColumnOrder,
      getMobileCardLayout,
      getMobileDrawerSections,
      setMobileLayoutPresetCallback,
      getMobileLayoutPresetCallback,
      getColumn,
      getCategoryColumns,
      getCategoryStats,
    ]
  );

  return <ColumnManagerContext.Provider value={value}>{children}</ColumnManagerContext.Provider>;
}

// Hook to use the column manager
export function useColumnManager() {
  const context = useContext(ColumnManagerContext);
  if (context === undefined) {
    throw new Error("useColumnManager must be used within a ColumnManagerProvider");
  }
  return context;
}
